/**
 * useMediaSession — Drives the OS notification/lock-screen media card.
 *
 * What this enables (when a retreat is running):
 *  - Android: persistent notification bar card with retreat name + session title
 *  - iOS (standalone): lock screen card with the same info
 *  - Both: a progress bar (setPositionState) showing retreat elapsed/total
 *  - Both: a "Stop" action button on the notification
 *
 * The metadata is refreshed every render so the session title (e.g. "Zazen · 45 min")
 * and progress bar update as the retreat advances — matching how Spotify updates
 * the song title and scrubber position on the lock screen.
 */
import { useEffect } from "react";
import { useRetreat } from "./useRetreat";
import { RetreatManager, RetreatState } from "@retreats/engine";

type LiveInfo = {
  sessionName: string;
  sessionKind: string;
  retreatName: string;
  elapsedSeconds: number;
  totalDurationSeconds: number;
};

export function useMediaSession(live?: LiveInfo): void {
  const snapshot = useRetreat();

  // Update metadata + position state on every render while running
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;

    switch (snapshot.status) {
      case RetreatState.RUNNING: {
        navigator.mediaSession.playbackState = "playing";

        const title = live?.sessionName ?? "Retreat in Progress";
        const artist = live?.sessionKind ?? "Retreat Conductor";
        const album = live?.retreatName ?? "Silent Retreat";

        // Always replace metadata so the title/subtitle update as sessions advance.
        // (Unlike music players, we can't use MediaMetadata.update() — recreate each time.)
        navigator.mediaSession.metadata = new MediaMetadata({
          title,
          artist,
          album,
          artwork: [
            { src: "/icon-192.png", sizes: "192x192", type: "image/png" },
            { src: "/icon-512.png", sizes: "512x512", type: "image/png" },
          ],
        });

        // Drive the notification progress bar / lock-screen scrubber
        if (live && live.totalDurationSeconds > 0 && navigator.mediaSession.setPositionState) {
          try {
            navigator.mediaSession.setPositionState({
              duration: live.totalDurationSeconds,
              playbackRate: 1,
              position: Math.min(live.elapsedSeconds, live.totalDurationSeconds),
            });
          } catch {
            // setPositionState can throw if duration < position — guard only
          }
        }
        break;
      }
      case RetreatState.IDLE:
        navigator.mediaSession.playbackState = "none";
        navigator.mediaSession.metadata = null;
        break;
      default:
        navigator.mediaSession.playbackState = "paused";
    }
  });

  // Wire action handlers once on mount
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const manager = RetreatManager.get();

    navigator.mediaSession.setActionHandler("play", () => { void manager.start(); });
    navigator.mediaSession.setActionHandler("stop", () => { void manager.stop(); });
    // No pause action (PV-13: no pause in v1 — retreat is a continuous flow)
    navigator.mediaSession.setActionHandler("pause", null);

    return () => {
      navigator.mediaSession.setActionHandler("play", null);
      navigator.mediaSession.setActionHandler("stop", null);
      navigator.mediaSession.metadata = null;
      navigator.mediaSession.playbackState = "none";
    };
  }, []);
}
